import { Box, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useFormikContext } from 'formik';
import { useState } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import { ChevronUp } from 'lucide-react';
import { CreateAptValues } from '../../utils/form/appointmentForm';
import { LoadedUser } from '../../interfaces';
import useUser from '../../hooks/global/useUser';
import { getCalendarTimezone } from '../../helpers/appointments';
import {
  convertMinsToHrsMins,
  formatDollarAmount,
  getArtistName,
  getFullTattooLocation,
} from '../../utils/global/helper';
import { getFullName } from '../../helpers/global';
import useCashDiscount from '../../hooks/payments/useCashDiscount';

interface Props {
  submitText: string;
  disabled: boolean;
  isSubmitting: boolean;
  onSubmit: () => void;
  // Docked bar for single-column layouts: one line, tap to reveal the rows.
  condensed?: boolean;
}

const mutedSx = { color: 'text.tertiary' };

function Row({ label, value }: { label: string; value: string }) {
  return (
    <Stack
      direction="row"
      alignItems="baseline"
      justifyContent="space-between"
      gap={2}
      paddingY={0.875}>
      <Typography variant="caption" flexShrink={0} sx={mutedSx}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        textAlign="right"
        minWidth={0}
        sx={{
          color: 'text.secondary',
          overflowWrap: 'anywhere',
        }}>
        {value}
      </Typography>
    </Stack>
  );
}

export default function AppointmentSummary({
  submitText,
  disabled,
  isSubmitting,
  onSubmit,
  condensed = false,
}: Props) {
  const { values } = useFormikContext<CreateAptValues>();
  const { user: loadedUser } = useUser<LoadedUser>();
  const { getAllInPrice } = useCashDiscount();
  const [expanded, setExpanded] = useState(false);

  const clientName = getFullName({
    firstName: values.firstName,
    lastName: values.lastName,
  });

  const employee = loadedUser?.business.employees.find(
    e => e.employeeId === values.employeeId,
  );

  const service = loadedUser?.business.services?.find(
    s => String(s.serviceId) === String(values.serviceId),
  );
  const serviceName = service?.serviceName || 'Tattoo';
  const tattooLocation = getFullTattooLocation(
    values.tattooLocation,
    values.bodySide,
  );
  const serviceValue = tattooLocation
    ? `${serviceName} · ${tattooLocation}`
    : serviceName;

  const length = Number(values.appointmentLengthInMinutes) || 0;
  const deposit = values.depositPrice ?? 0;

  const isConsultation = values.appointmentType === 'CONSULTATION';
  const showTotal = !isConsultation && !values.isTouchUp;

  // The all-in price, so the total matches what the client is asked to pay.
  const prices =
    values.priceType === 'HOURLY'
      ? [values.hourlyPrice]
      : values.priceType === 'RANGE'
      ? [values.minPrice, values.maxPrice]
      : [values.price];
  const total = `${prices
    .map(price => formatDollarAmount(getAllInPrice(price), true))
    .join(' - ')}${values.priceType === 'HOURLY' ? '/hr' : ''}`;

  const whenIsSet = !!values.startDateTime || values.isBookingLink;
  const whenText = values.isBookingLink
    ? 'Client picks a date & time'
    : values.startDateTime
    ? formatInTimeZone(
        new Date(values.startDateTime),
        getCalendarTimezone(loadedUser ?? undefined, values.businessTripId),
        "MMM d 'at' h:mm a",
      )
    : 'Date & time not set';

  // Scheduled waivers go out separately, an hour before the appointment.
  const owesDeposit = deposit > 0 && !values.depositPaid;
  const notice =
    values.appointmentType === 'WALK_IN'
      ? values.requestWaiver
        ? 'complete the waiver'
        : null
      : values.isBookingLink
      ? `pick a time${owesDeposit ? ' and pay the deposit' : ''}`
      : `confirm${owesDeposit ? ' and pay the deposit' : ''}`;

  const rows = (
    <>
      <Row label="Service" value={serviceValue} />
      {!!length && <Row label="Length" value={convertMinsToHrsMins(length)} />}
      {deposit > 0 && (
        <Row
          label="Deposit"
          value={`${formatDollarAmount(getAllInPrice(deposit), true)} ${
            values.depositPaid ? 'already paid' : 'due at booking'
          }`}
        />
      )}
      <Row
        label="Waiver"
        value={values.requestWaiver ? 'Through Porter' : 'Not collected'}
      />
    </>
  );

  const submitButton = (
    <LoadingButton
      type="button"
      variant="contained"
      className="primary"
      fullWidth
      disabled={disabled}
      loading={isSubmitting}
      onClick={onSubmit}
      sx={{ height: 52, borderRadius: '50px', fontSize: 'h3.fontSize' }}>
      {submitText}
    </LoadingButton>
  );

  const footnote = !!notice && (
    <Typography
      variant="caption"
      component="p"
      textAlign="center"
      lineHeight={1.4}
      marginTop={1.25}
      sx={mutedSx}>
      {values.firstName || 'The client'} gets a text with a link to {notice}.
    </Typography>
  );

  if (condensed) {
    const headline = [
      clientName || 'No client selected',
      employee && getArtistName(employee),
    ]
      .filter(Boolean)
      .join(' · ');

    return (
      <Box
        sx={{
          borderTop: 1,
          borderColor: 'var(--lineGrey)',
          background: theme => theme.palette.common.white,
          padding: '12px 16px',
          paddingBottom: 'calc(16px + env(safe-area-inset-bottom))',
        }}>
        {expanded && <Box paddingBottom={1.5}>{rows}</Box>}
        <Box
          component="button"
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded(open => !open)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            width: '100%',
            padding: '0 0 12px',
            border: 0,
            background: 'none',
            font: 'inherit',
            textAlign: 'left',
            cursor: 'pointer',
          }}>
          <Box flex={1} minWidth={0}>
            <Typography
              variant="h4"
              noWrap
              sx={{
                color: theme =>
                  clientName
                    ? theme.palette.common.black
                    : theme.palette.text.tertiary,
              }}>
              {headline}
            </Typography>
            <Typography
              variant="body2"
              marginTop="2px"
              sx={{
                color: theme =>
                  whenIsSet ? 'text.secondary' : theme.palette.primary.main,
              }}>
              {whenText}
            </Typography>
          </Box>
          {showTotal && (
            <Typography
              variant="h4"
              flexShrink={0}
              sx={{ color: theme => theme.palette.common.black }}>
              {total}
            </Typography>
          )}
          <Box
            component="span"
            sx={{
              display: 'flex',
              flexShrink: 0,
              color: 'text.tertiary',
              transform: expanded ? 'rotate(180deg)' : 'none',
              transition: 'transform 150ms ease',
            }}>
            <ChevronUp size={16} strokeWidth={2} />
          </Box>
        </Box>
        {submitButton}
        {footnote}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        border: 1,
        borderColor: theme => theme.palette.common.black,
        borderRadius: '10px',
        background: theme => theme.palette.common.white,
        overflow: 'hidden',
      }}>
      <Box padding="18px 20px 16px">
        <Typography
          variant="caption"
          component="p"
          marginBottom={1.75}
          sx={mutedSx}>
          Summary
        </Typography>
        <Typography
          variant="h2"
          lineHeight={1.25}
          sx={{
            overflowWrap: 'anywhere',
            ...(clientName
              ? { color: theme => theme.palette.common.black }
              : mutedSx),
          }}>
          {clientName || 'No client selected'}
        </Typography>
        {!!employee && (
          <Typography
            variant="body1"
            marginTop={0.75}
            sx={{ overflowWrap: 'anywhere' }}>
            with {getArtistName(employee)}
          </Typography>
        )}
        <Typography
          variant="body1"
          marginTop={0.5}
          sx={{
            color: theme =>
              whenIsSet ? 'text.primary' : theme.palette.primary.main,
          }}>
          {whenText}
        </Typography>
      </Box>

      <Box padding="0 20px 16px">
        {rows}
        {showTotal && (
          <Stack
            direction="row"
            alignItems="baseline"
            justifyContent="space-between"
            gap={2}
            marginTop={0.75}
            paddingTop={1.5}
            sx={{
              borderTop: 1,
              borderColor: 'var(--lineGrey)',
            }}>
            <Typography variant="body2">Total</Typography>
            <Typography
              variant="h4"
              sx={{ color: theme => theme.palette.common.black }}>
              {total}
            </Typography>
          </Stack>
        )}
      </Box>

      <Box
        padding="16px 20px"
        sx={{
          borderTop: 1,
          borderColor: 'var(--lineGrey)',
          background: '#f9f9f9',
        }}>
        {submitButton}
        {footnote}
      </Box>
    </Box>
  );
}
